import React, { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  ModalRules,
  OptinCard,
  ModalConfirmation,
  ModalSuccess,
  ModalError,
  ModalAlreadyRegistered,
} from '../../components';
import { VoeBizApi } from '../../services/services';
import { OptinProps } from '../../types/types';

type ResponsePromoDataType = {
  items: Array<{
    contentFields: Array<{
      contentFieldValue: { [key: string]: any };
      dataType: string;
      label: string;
      name: string;
      repeatable: boolean;
      nestedContentFields: NestedContentFields;
    }>;
  }>;
};

type NestedContentFields = Array<{
  contentFieldValue: { [key: string]: any };
  name: string;
  label: string;
  dataType: string;
  nestedContentFields: NestedContentFields;
}>;

const OptIn = ({ promoId, promoContent, promoRule, promoLink }: OptinProps) => {
  const ldFlags = useFlags();
  const { apiDomain } = ldFlags;

  const api = VoeBizApi();
  const [isOpenModalRules, setIsOpenModalRules] = useState(false);
  const [isOpenModalConfirmation, setIsOpenModalConfirmation] = useState(false);
  const [isOpenModalSuccess, setIsOpenModalSuccess] = useState(false);
  const [isOpenModalError, setIsOpenModalError] = useState(false);
  const [isOpenModalAlreadyRegistered, setIsOpenModalAlreadyRegistered] = useState(false);
  const [contentData, setContentData] = useState<string>('');
  const [rulesData, setRulesData] = useState<string>('');

  const toggleModalSuccess = () => setIsOpenModalSuccess(!isOpenModalSuccess);
  const toggleModalAlreadyRegistered = () => setIsOpenModalAlreadyRegistered(!isOpenModalAlreadyRegistered);
  const toggleModalError = () => setIsOpenModalError(!isOpenModalError);
  const toggleModalRules = () => setIsOpenModalRules(!isOpenModalRules);
  const toggleModalConfirmation = () => setIsOpenModalConfirmation(!isOpenModalConfirmation);

  const handleOpenModalRules = () => {
    setIsOpenModalRules(true);
    setIsOpenModalConfirmation(false);
  };

  const handleOpenModalConfirmation = () => {
    setIsOpenModalConfirmation(true);
    setIsOpenModalRules(false);
  };

  const getLiferayHtmlContent = async (liferayDomain: string, siteId: string, contentId: string) => {
    try {
      const response = await fetch(
        `${liferayDomain}/o/headless-delivery/v1.0/sites/${siteId}/structured-contents?flatten=true&filter=friendlyUrlPath eq '${contentId}'`,
      );
      if (!response.ok) {
        return '';
      }

      const responseJson: ResponsePromoDataType = await response.json();
      if (!responseJson.items || !responseJson.items[0].contentFields.length) {
        return '';
      }

      const contentFieldValue = responseJson.items[0].contentFields[0].contentFieldValue;
      const htmlContent = contentFieldValue.data;
      return htmlContent;
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  useEffect(() => {
    if (ldFlags.apiDomain) {
      const getHtmlContents = async () => {
        let liferayDomain = '';
        if (window.location.hostname === 'localhost') {
          liferayDomain = ldFlags.apiDomain.replace('apigw-voebiz', 'voebiz').replace('hml', 'uat');
        }

        const promoContentHtml = await getLiferayHtmlContent(liferayDomain, ldFlags.liferaySiteId, promoContent);
        setContentData(promoContentHtml);

        const promoRuleHtml = await getLiferayHtmlContent(liferayDomain, ldFlags.liferaySiteId, promoRule);
        setRulesData(promoRuleHtml);
      };
      getHtmlContents();
    }
  }, [ldFlags.apiDomain]);

  const getPromotionData = async (apiDomainValue: string, promotionalIdValue: string, cnpj: string) => {
    const resultGetPromotionData = await api.getPromotion(apiDomainValue, promotionalIdValue, cnpj);
    // console.log('data', resultGetPromotionData);
    if (resultGetPromotionData) {
      if (resultGetPromotionData.success) {
        setIsOpenModalSuccess(true);
        return;
      }
      const userMessageObjectMsgError = resultGetPromotionData.response.response.data.data.user_message;
      if (userMessageObjectMsgError.includes('errorCode')) {
        const userMessageObjectErrorCode = JSON.parse(userMessageObjectMsgError);
        const errorCode = userMessageObjectErrorCode.errorCode;
        if (errorCode === '140') {
          setIsOpenModalAlreadyRegistered(true);
          return;
        }
      }
      setIsOpenModalError(true);
    }
    setIsOpenModalError(true);
  };

  const handleContinue = async (cnpjValue: string) => {
    await getPromotionData(apiDomain, promoId, cnpjValue);
  };

  return (
    <>
      <div className="optin-card-container" data-testid="optin">
        <OptinCard onClick={handleOpenModalConfirmation} promoContent={contentData} />
        <ModalRules
          isOpenModalRules={isOpenModalRules}
          toggleModalRules={toggleModalRules}
          promoRule={rulesData}
          onClick={handleOpenModalConfirmation}
        />
        <ModalConfirmation
          isOpenModalConfirmation={isOpenModalConfirmation}
          toggleModalConfirmation={toggleModalConfirmation}
          onClickContinue={handleContinue}
          onClickRule={handleOpenModalRules}
          promoLink={promoLink}
        />

        <ModalSuccess isOpenModalSuccess={isOpenModalSuccess} toggleModalSuccess={toggleModalSuccess} />

        <ModalError isOpenModalError={isOpenModalError} toggleModalError={toggleModalError} />

        <ModalAlreadyRegistered
          isOpenModalAlreadyRegistered={isOpenModalAlreadyRegistered}
          toggleModalAlreadyRegistered={toggleModalAlreadyRegistered}
        />
      </div>
    </>
  );
};

export default OptIn;
